// Minimal, dependency-free JWT helpers. The frontend only ever needs to READ
// the (unverified) `exp` claim to detect a timed-out session — signature
// verification is the backend's job — so we decode the payload by hand rather
// than pulling in a library.

// Decode a base64url string to UTF-8 JSON-safe text.
function base64UrlDecode(segment) {
  let base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  // Restore '=' padding so atob is happy regardless of runtime strictness.
  while (base64.length % 4) base64 += '=';
  return decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
}

/**
 * Return the token's expiry as epoch milliseconds, or null when the token is
 * missing, malformed, or carries no numeric `exp` claim.
 */
export function getTokenExpiryMs(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    if (typeof payload.exp !== 'number') return null;
    return payload.exp * 1000;
  } catch (_e) {
    return null;
  }
}

/**
 * True only when the token has a decodable `exp` that is already in the past.
 *
 * Fail-open: a missing/malformed/exp-less token returns false (treated as
 * not-expired) so the reactive 401 interceptor remains the backstop and we
 * never log out a session we can't actually prove has expired.
 *
 * `skewMs` adds a grace period so minor client-clock drift can't log out a
 * session that is still valid on the server.
 */
export function isTokenExpired(token, skewMs = 0) {
  const expiryMs = getTokenExpiryMs(token);
  if (expiryMs === null) return false;
  return expiryMs + skewMs <= Date.now();
}
